/*
  useVersionCheck
  - Comprueba `${import.meta.env.BASE_URL}/version.json` al montar y cada 5 minutos.
  - Lanza una comprobación inmediata al recuperar el foco de la pestaña (visibilitychange).
  - Si la versión remota difiere de la actual, abre un modal (i18n) para recargar; en "OK" recarga la página.
  - Desduplica el aviso por versión y entre pestañas usando localStorage + evento "storage"; sólo se muestra una vez por versión.
  - Si el usuario cancela, no vuelve a avisar por esa misma versión (hasta que haya una nueva).
  - Ignora errores de red y limpia intervalo y listeners al desmontar.
*/

import { ref, onMounted, onUnmounted } from "vue";
import { useModalService } from "@services/modal-service";
import { useIntl } from "@services/i18n/i18nservice";

const VERSION_URL = `${import.meta.env.BASE_URL}/version.json`;
const CHECK_INTERVAL = 5 * 60 * 1000; // cada 5 minutos
const MODAL_SHOWN_KEY = "app.versionCheck.modalShownForVersion";

export function useVersionCheck() {
  const currentVersion = ref<string | null>(null);
  const modalShownForVersion = ref<string | null>(null);
  const modalService = useModalService();
  const intl = useIntl();
  let intervalId: number | undefined;

  const checkVersion = async () => {
    try {
      const res = await fetch(VERSION_URL, { cache: "no-store" });
      const data = await res.json();
      if (currentVersion.value && data.version !== currentVersion.value && modalShownForVersion.value !== data.version) {
        modalShownForVersion.value = data.version;
        try {
          localStorage.setItem(MODAL_SHOWN_KEY, data.version);
        } catch {}

        const result = await modalService.messageBox(
          intl.i18n.t("shared.ui.versionCheck.message"),
          intl.i18n.t("shared.ui.versionCheck.title"),
          "okCancel",
          'info',
          true
        );
        if (result === "ok") {
          window.location.reload();
        }
      } else if (!currentVersion.value) {
        currentVersion.value = data.version;
      }
    } catch (e) {
      // Ignoramos errores de red
    }
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      checkVersion();
    }
  };
  const handleStorage = (e: StorageEvent) => {
    if (e.key === MODAL_SHOWN_KEY) {
      modalShownForVersion.value = e.newValue;
    }
  };

  onMounted(() => {
    try {
      modalShownForVersion.value = localStorage.getItem(MODAL_SHOWN_KEY);
    } catch {}
    checkVersion();
    intervalId = window.setInterval(checkVersion, CHECK_INTERVAL);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("storage", handleStorage);
  });

  onUnmounted(() => {
    if (intervalId) clearInterval(intervalId);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("storage", handleStorage);
  });
}